import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { azn, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Sparkles, Gift, Clock } from "lucide-react";

const SEG = 60; // 6 segments of 60deg

export default function Wheel() {
  const { user, refresh } = useAuth();
  const [prizes, setPrizes] = useState([0.5, 1, 2, 3, 5, 10]);
  const [canSpin, setCanSpin] = useState(false);
  const [lastPrize, setLastPrize] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [wonPrize, setWonPrize] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/wheel/status");
      setPrizes(data.prizes);
      setCanSpin(data.can_spin);
      setLastPrize(data.last_prize);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadStatus(); }, []);

  const spin = async () => {
    if (!canSpin || spinning) return;
    setSpinning(true);
    setWonPrize(null);
    try {
      const { data } = await api.post("/wheel/spin");
      const idx = data.index;
      const targetCenter = idx * SEG + SEG / 2;         // clockwise angle of winning segment centre
      const jitter = Math.random() * 30 - 15;           // stay inside the segment
      const desired = (360 - targetCenter + jitter + 360) % 360;
      const current = rotation % 360;
      const delta = 360 * 6 + ((desired - current + 360) % 360);
      setRotation((r) => r + delta);
      setTimeout(async () => {
        setWonPrize(data.prize);
        toast.success(`Təbriklər! ${azn(data.prize)} qazandınız 🎉`);
        setCanSpin(false);
        setLastPrize(data.prize);
        await refresh();
        setSpinning(false);
      }, 4300);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSpinning(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/15 text-accent text-sm mb-3">
            <Sparkles className="w-4 h-4" /> Gündə 1 dəfə pulsuz
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Şanslı Çarx</h1>
          <p className="text-muted-foreground mt-2">Çarxı fırlat və 0.5 ₼-dən 10 ₼-dək bonus qazan!</p>
        </div>

        <div className="flex flex-col items-center">
          {/* Wheel */}
          <div className="relative w-[300px] h-[300px] sm:w-[360px] sm:h-[360px]" data-testid="wheel">
            {/* Pointer */}
            <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-20">
              <div className="w-0 h-0 border-l-[14px] border-r-[14px] border-t-[26px] border-l-transparent border-r-transparent border-t-accent drop-shadow-lg" />
            </div>

            <div
              className="absolute inset-0 rounded-full border-8 border-primary shadow-2xl overflow-hidden"
              style={{
                transform: `rotate(${rotation}deg)`,
                transition: spinning ? "transform 4.2s cubic-bezier(0.15, 0.85, 0.2, 1)" : "none",
                background: `conic-gradient(
                  hsl(var(--primary)) 0deg 60deg,
                  hsl(var(--accent)) 60deg 120deg,
                  hsl(var(--primary)) 120deg 180deg,
                  hsl(var(--accent)) 180deg 240deg,
                  hsl(var(--primary)) 240deg 300deg,
                  hsl(var(--accent)) 300deg 360deg
                )`,
              }}
            >
              {prizes.map((p, i) => {
                const deg = i * SEG + SEG / 2;          // centre angle of the wedge
                const theta = (deg * Math.PI) / 180;
                const r = 34;                            // radius in % from centre
                const x = 50 + r * Math.sin(theta);
                const y = 50 - r * Math.cos(theta);
                return (
                  <div
                    key={i}
                    className="absolute font-heading font-bold text-lg text-white drop-shadow whitespace-nowrap"
                    style={{ left: `${x}%`, top: `${y}%`, transform: `translate(-50%, -50%) rotate(${deg}deg)` }}
                    data-testid={`wheel-seg-${i}`}
                  >
                    {p} ₼
                  </div>
                );
              })}
            </div>

            {/* Hub */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-16 h-16 rounded-full bg-background border-4 border-primary flex items-center justify-center shadow-lg">
              <Gift className="w-7 h-7 text-primary" />
            </div>
          </div>

          {/* Controls */}
          <div className="mt-10 text-center">
            {canSpin ? (
              <Button
                onClick={spin}
                disabled={spinning}
                className="rounded-full h-14 px-12 text-lg bg-accent text-accent-foreground hover:bg-accent/90 transition-transform hover:scale-[1.03]"
                data-testid="spin-btn"
              >
                {spinning ? "Fırlanır..." : "Çarxı Fırlat 🎡"}
              </Button>
            ) : (
              <div className="flex flex-col items-center gap-2" data-testid="already-spun">
                <div className="flex items-center gap-2 px-5 py-3 rounded-full bg-secondary text-secondary-foreground">
                  <Clock className="w-4 h-4" />
                  <span>Bu gün fırlatdınız. Sabah yenidən cəhd edin!</span>
                </div>
                {lastPrize != null && (
                  <p className="text-sm text-muted-foreground">Son qazanc: <b className="text-accent">{azn(lastPrize)}</b></p>
                )}
              </div>
            )}
            {wonPrize != null && (
              <p className="mt-4 font-heading text-2xl font-bold text-accent animate-float-up" data-testid="won-prize">
                +{azn(wonPrize)} balansınıza əlavə edildi!
              </p>
            )}
          </div>

          {/* Prize legend */}
          <div className="mt-10 grid grid-cols-3 sm:grid-cols-6 gap-3 w-full">
            {prizes.map((p, i) => (
              <div key={i} className="rounded-xl border border-border bg-card py-3 text-center">
                <p className="font-heading font-bold">{p} ₼</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
